import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ApprovalLevelRow } from '@risksmart-app/drizzle/src/dto';
import type {
  ChangeRequestPartsFragment,
  MyItemsChangeRequestPartsFragment,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { Approval_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import groupBy from 'lodash/groupBy';
import { useEffect, useMemo } from 'react';
import { useGetChangeRequestsByParentSubscription } from 'src/hooks/queries/change-request/useGetChangeRequestsByParentSubscription';
import { useGetLivePendingChangeRequestsSubscription } from 'src/hooks/queries/change-request/useGetLivePendingChangeRequestsSubscription';
import type { LabelledIdArray } from 'src/rbac/types';

import type { ChangeRequestResponses } from '@/utils/changeRequestUtils';
import {
  getCurrentApprovers,
  getCurrentLevel,
  getMaxLevel,
  getNextApprovers,
} from '@/utils/changeRequestUtils';

import { useGetPendingChangeRequests } from './queries';

export type ObjectWithApprovals = {
  Id: string;
};

type ApprovalCheckChangeRequestFragment =
  | MyItemsChangeRequestPartsFragment
  | Pick<ChangeRequestPartsFragment, 'ChangeRequestStatus' | 'responses'>;

type UseChangeRequestResult = {
  pendingChangeRequests: ChangeRequestPartsFragment[];
  pendingDeleteRequests: ChangeRequestPartsFragment[];
  isActiveApprover: (
    changeRequest: ApprovalCheckChangeRequestFragment,
    ownerIds?: string[]
  ) => boolean;
  getCurrentApprovers: (responses: ChangeRequestResponses) => LabelledIdArray;
  getNextApprovers: (responses: ChangeRequestResponses) => LabelledIdArray;
  getCurrentLevel: (responses: ChangeRequestResponses) => number;
  getMaxLevel: (responses: ChangeRequestResponses) => number;
  activeLevelId: (changeRequest: ChangeRequestPartsFragment) => null | string;
  canAmendChangeRequest: (changeRequest: ChangeRequestPartsFragment) => boolean;
  changeRequests: ChangeRequestPartsFragment[];
  loading: boolean;
};

export const getOwners = (
  changeRequest: ChangeRequestPartsFragment
): string[] | undefined => {
  if (changeRequest.parent?.ObjectType === 'document_file') {
    return changeRequest.parent?.documentFile?.parent?.owners?.map(
      (owner) => owner.UserId ?? ''
    );
  }
  if (changeRequest.parent?.ObjectType === 'issue_assessment') {
    return changeRequest.parent?.issue_assessment?.parent?.owners?.map(
      (owner) => owner.UserId ?? ''
    );
  }
  if (changeRequest.parent?.ObjectType === 'acceptance') {
    return changeRequest.parent?.acceptance?.parents
      ?.filter((p) => p.risk)
      .map((p) => p.risk?.owners.map((owner) => owner.UserId ?? '') ?? [])
      .flat(2);
  }

  return changeRequest.parent?.owners?.map((owner) => owner.UserId ?? '');
};

/**
 * Sort key aligning the ACTIVE-level walk with the panel's DISPLAY order
 * (`getApprovalLevels`: per-entity approval's levels first, then the
 * GLOBAL approval's, SequenceOrder-asc within each). Without it the walk
 * follows `Object.values(groupBy(responses))` insertion order — i.e. the
 * raw responses array, which is seeding/query order — so with 1 object +
 * 2 global levels the first approvable level could be the global one the
 * panel displays as level 2 (RSP-6087 sequencing bug).
 *
 * Tolerant of slim level shapes (MyItems fragment has no SequenceOrder /
 * approval): missing fields rank equal and the stable sort preserves the
 * previous insertion order there. The null branch is a defensive default —
 * the synthetic NO-LEVEL bucket is excluded from the walk before sorting.
 */
const levelOrderKey = (
  level:
    | {
        Id?: string;
        SequenceOrder?: number | null;
        approval?: { ParentId?: string | null } | null;
      }
    | null
    | undefined
): [number, number] => {
  if (!level) {
    return [2, 0];
  }
  const globalRank =
    level.approval === undefined
      ? 0
      : (level.approval?.ParentId ?? null) === null
        ? 1
        : 0;

  return [globalRank, level.SequenceOrder ?? 0];
};

export const computeActiveLevelId = (
  changeRequest: ApprovalCheckChangeRequestFragment
) => {
  // group approver responses by levels
  const levels = groupBy(
    changeRequest.responses,
    (response) => response.approver.level?.Id ?? 'NO-LEVEL'
  );
  let rejected = false;

  const levelResults: boolean[] = [];

  // Walk levels in display order (specific-first, SequenceOrder-asc) so
  // "first unsatisfied level" matches the level the panel labels first.
  // The synthetic NO-LEVEL bucket (responses whose approver has no level) is
  // EXCLUDED from the walk: it has no ApprovalRuleType so the switch below
  // would push nothing for it, skewing `levelResults` indices against
  // `orderedLevelIds` — and a level-less response can't be voted on anyway.
  const orderedLevelIds = Object.keys(levels)
    .filter((levelId) => levelId !== 'NO-LEVEL')
    .sort((a, b) => {
      const [globalA, seqA] = levelOrderKey(levels[a][0]?.approver.level);
      const [globalB, seqB] = levelOrderKey(levels[b][0]?.approver.level);

      return globalA - globalB || seqA - seqB;
    });

  orderedLevelIds.forEach((levelId) => {
    // No empty-group guard needed: lodash groupBy only creates a key when at
    // least one response hashed to it, so every group here is non-empty.
    const level = levels[levelId];

    const responded = level.filter(
      (response) => response.Approved !== null
    ).length;
    const approvers = level.filter(
      (response) => response.Approved === true
    ).length;

    // Approval rule decisions
    switch (level[0].approver.level?.ApprovalRuleType) {
      case 'all_approve':
        // If the number of approvers is equal to the number of responses, then approve.
        levelResults.push(approvers === level.length);
        // If the number of responses is more than the number of approvers, then reject
        // since someone must have rejected it.
        if (responded > approvers) {
          rejected = true;
        }
        break;
      case 'any_one_approve':
        // If there is at least one approver, then approve
        levelResults.push(approvers > 0);
        // If everyone has responded and there are no approvers, then reject.
        if (responded === level.length && approvers === 0) {
          rejected = true;
        }
        break;
      case 'majority_approve':
        // If the number of approvers is more than half, then approve.
        levelResults.push(approvers > level.length / 2);
        // If at least half of the approvers have rejected, then reject since
        // it will be impossible to make a majority.
        if (responded - approvers >= level.length / 2) {
          rejected = true;
        }
        break;
      default:
        // Invariant: EVERY walked level pushes exactly one result —
        // `firstNonRejectedLevelIndex` indexes back into `orderedLevelIds`,
        // so a skipped push would skew the mapping. An unrecognised rule
        // type (impossible at write time — the schemas are enum-constrained
        // — but cheap to defend) counts as satisfied: the panel can't
        // render voting controls for a rule it can't evaluate.
        levelResults.push(true);
        break;
    }
  });

  const firstNonRejectedLevelIndex = levelResults.findIndex(
    (result) => !result
  );
  const activeLevelIndex =
    rejected || firstNonRejectedLevelIndex === -1
      ? null
      : firstNonRejectedLevelIndex;

  return activeLevelIndex !== null ? orderedLevelIds[activeLevelIndex] : null;
};

/**
 * A level the approval-steps panel renders. `Id` keys the per-level response
 * group; `ApprovalRuleType` is carried for parity with the configured-levels
 * row (the panel does not currently read it, but downstream consumers / tests
 * may rely on the shape matching `ApprovalLevelRow`).
 */
export type DisplayLevel = {
  Id: string;
  ApprovalRuleType: string;
};

/**
 * The subset of an approver-response shape `getDisplayLevels` reads. A
 * structural type (not `ChangeRequestPartsFragment['responses'][number]`) so
 * the helper is callable with any response carrying the level chain — the full
 * fragment is a superset and assigns cleanly.
 */
type DisplayLevelResponse = {
  approver: {
    level?: {
      Id?: string;
      SequenceOrder?: number | null;
      ApprovalRuleType?: string | null;
      approval?: { ParentId?: string | null } | null;
    } | null;
  };
};

/**
 * Resolve the ordered set of levels the approval-steps panel renders for a CR
 * (RSP-6437).
 *
 * Every level PRESENT on the CR's responses is rendered — never dropped into the
 * panel's "No Level" bucket (which shows no approve/reject controls). All levels
 * are ordered by the SAME `levelOrderKey` the active-level walk uses
 * (specific-first, then SequenceOrder), so the panel's display order — and its
 * "Level N" labels — stay aligned with `computeActiveLevelId`'s active-level
 * selection. That key reproduces the backend's `getApprovalLevels` ordering
 * (specific-then-global), so the configured-levels case is unchanged from
 * RSP-6087; `configuredLevels` is now only consulted for each level's
 * `ApprovalRuleType`.
 *
 * Why a response level can be missing from `configuredLevels`: the lookup
 * resolves a SINGLE (workflow, parentId) pair, and the panel derives that
 * parentId from the first response carrying a non-null approval ParentId. A CR
 * whose responses span more than one approval parent — e.g. a per-entity
 * approval configured on the document PLUS the org-wide GLOBAL approval (the
 * exact mixed config in RSP-6437) — can therefore have configured levels that
 * miss some of its own response levels. Before this, those responses fell into
 * "No Level" and an approver eligible only via the missed level (e.g. a member
 * of the global approval group) saw the CR but no approve/reject buttons.
 */
export const getDisplayLevels = (
  responses: DisplayLevelResponse[],
  configuredLevels: ApprovalLevelRow[] | undefined
): DisplayLevel[] => {
  // First response seen per level — carries the level chain `levelOrderKey`
  // needs (SequenceOrder + approval.ParentId), de-duplicating shared levels and
  // preserving first-seen order as the stable-sort tiebreaker.
  const levelById = new Map<
    string,
    DisplayLevelResponse['approver']['level']
  >();
  for (const response of responses) {
    const level = response.approver.level;
    if (level?.Id && !levelById.has(level.Id)) {
      levelById.set(level.Id, level);
    }
  }

  // Index configured levels by Id once so the per-level `ApprovalRuleType`
  // lookup below is O(1) rather than a linear `find` over `configuredLevels`
  // for every rendered level.
  const configuredLevelById = new Map(
    configuredLevels?.map((configured) => [configured.Id, configured])
  );

  return [...levelById.entries()]
    .sort(([, levelA], [, levelB]) => {
      const [globalA, seqA] = levelOrderKey(levelA);
      const [globalB, seqB] = levelOrderKey(levelB);

      return globalA - globalB || seqA - seqB;
    })
    .map(([levelId, level]) => ({
      Id: levelId,
      ApprovalRuleType:
        configuredLevelById.get(levelId)?.ApprovalRuleType ??
        level?.ApprovalRuleType ??
        '',
    }));
};

export const useChangeRequests = (
  objectWithApprovals?: ObjectWithApprovals
): UseChangeRequestResult => {
  const { user, isLoading } = useRisksmartUser();
  const userId = user?.userId;
  // ALL change-request reads go through the holding-pattern subscription hooks
  // (`use*Subscription`): trpc ON → polled tRPC feed mapped V3→V2 (the only
  // surface that reshapes the V3 `RequestedChanges` blob — plain id arrays — into
  // the legacy `Owners`/`tags`/`departments` rows the forms were built against,
  // RSP-6099); trpc OFF → the legacy Hasura subscription. No raw Apollo here —
  // the poll→tRPC-SSE swap (RSP-4320) lives in `createSubscriptionHook`.
  const { data, refetch } = useGetPendingChangeRequests({
    queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
    shouldSkip: !objectWithApprovals?.Id,
  });

  // `data` (a QUERY hook) and `liveData` (a SUBSCRIPTION hook) intentionally
  // read the SAME pending-CR data through two transports — this mirrors the V2
  // design they replace: a one-shot `GetPendingChangeRequests` query plus a live
  // `GetLivePendingChangeRequests` subscription. The query is the authoritative
  // snapshot; the subscription is the live channel. The effect below bridges
  // them: when the live channel drains to empty but the snapshot still shows
  // rows (the merge consumer just settled the CR on another actor), it refetches
  // the snapshot so the two converge. Under `trpc` ON both currently poll the
  // same procedure (no SSE transport yet, RSP-4320) — a known redundancy kept
  // deliberately so flag-OFF behaviour and this convergence are unchanged; it
  // collapses to one feed when the real tRPC subscription transport lands.
  const { data: liveData, loading: liveLoading } =
    useGetLivePendingChangeRequestsSubscription({
      queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
      shouldSkip: !objectWithApprovals?.Id,
    });
  const { data: byParentData, loading } =
    useGetChangeRequestsByParentSubscription({
      queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
      shouldSkip: !objectWithApprovals?.Id,
    });
  const allChangeRequests = byParentData?.change_request;

  useEffect(() => {
    if (
      liveData?.change_request &&
      liveData.change_request.length < 1 &&
      (data?.change_request.length ?? 0) > 0
    ) {
      refetch();
    }
  }, [liveData, data, refetch]);

  const activeLevelId = computeActiveLevelId;

  const isActiveApprover = (
    changeRequest: ApprovalCheckChangeRequestFragment,
    ownerIds?: string[]
  ) => {
    const levelId = activeLevelId(changeRequest);
    const responses = changeRequest.responses.filter(
      (response) => response.approver.level?.Id === levelId
    );

    const isReferencedActiveApprover = responses.some(
      (response) =>
        (response.approver.user?.Id === userId ||
          response.approver.group?.users.some(
            (user) => user?.UserId === userId
          )) &&
        response.Approved === null
    );
    const ownerApprover = responses.some(
      (response) =>
        response.approver.OwnerApprover && response.Approved === null
    );
    const isOwner = ownerIds?.includes(userId!) ?? false;

    return (
      ((ownerApprover && isOwner) || isReferencedActiveApprover) &&
      changeRequest.ChangeRequestStatus === Approval_Status_Enum.Pending
    );
  };

  const canAmendChangeRequest = (changeRequest: ChangeRequestPartsFragment) => {
    const levelId = activeLevelId(changeRequest);
    const level = changeRequest.responses.find(
      (response) => response.approver.level?.Id === levelId
    )?.approver.level;
    const rule = level?.approval?.InFlightEditRule;

    switch (rule) {
      case 'everyone':
        return true;
      case 'approvers':
        return isActiveApprover(changeRequest, getOwners(changeRequest));
      case 'noone':
        return false;
      default:
        return false;
    }
  };

  const pendingChangeRequests = useMemo(
    () =>
      liveData?.change_request.filter((cr) => cr.Type === 'update') ??
      data?.change_request.filter((cr) => cr.Type === 'update') ??
      [],
    [data?.change_request, liveData?.change_request]
  );
  const pendingDeleteRequests = useMemo(
    () =>
      liveData?.change_request.filter((cr) => cr.Type === 'delete') ??
      data?.change_request.filter((cr) => cr.Type === 'delete') ??
      [],
    [data?.change_request, liveData?.change_request]
  );

  const changeRequests = useMemo(
    () => allChangeRequests ?? [],
    [allChangeRequests]
  );

  return {
    loading: loading || liveLoading || isLoading,
    isActiveApprover,
    activeLevelId,
    canAmendChangeRequest,
    getCurrentApprovers,
    getNextApprovers,
    getCurrentLevel,
    getMaxLevel,
    pendingDeleteRequests,
    pendingChangeRequests,
    changeRequests,
  };
};
